/**
 * @fileoverview Own lead-queue navigation, filtering, pagination, previews, and selected bulk/pipeline actions. Backend queries define the result universe; browser preferences define its presentation. `Queue/intakeQueueUtils.js` owns shared filter/preview semantics and `intakeWorkspaceState.js` owns navigation memory. `IntakeLeadDetail.js` owns full lead actions. Queue refresh, export, and preview remain read operations separate from explicit execution controls.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ToastContext from '../../ToastContext';
import { fetchIntakeLeadPreview, fetchIntakeLeads, fetchIntakeOptions, runIntakePipeline, runIntakeStage } from './intakeApi';
import {
  formatDate,
  getDuplicateStatusClass,
  getDuplicateStatusLabel,
  getPromotionBatchStatusLabel,
  getRunPipelineActionLabel,
  getRunPipelineActionTitle
} from './intakeFormatters';
import { ACTIVE_STATUSES, COMPLETE_STATUSES, FAILED_RUN_STATUSES, REVIEW_RUN_STATUSES } from './intakeStatuses';
import { useAssignedAttorneyReviewCount } from './useAssignedAttorneyReviewCount';
import IntakeViewSwitch from './IntakeViewSwitch';
import { readIntakeViewState, writeIntakeViewState } from './intakeWorkspaceState';
import AppIcon from "../shared/AppIcon";
import {
  DEFAULT_FILTERS,
  FALLBACK_STATUSES,
  FALLBACK_STAGES,
  ROWS_PER_PAGE_OPTIONS,
  COLUMNS_KEY,
  COLUMNS_VERSION_KEY,
  COLUMNS_VERSION,
  TABLE_STATE_KEY,
  TABLE_STATE_VERSION_KEY,
  TABLE_STATE_VERSION,
  INTAKE_COLUMNS,
  DEFAULT_COLUMN_KEYS,
  ACTION_COLUMN_WIDTH,
  DEFAULT_QUEUE_VIEW,
  BOARD_PAGE_SIZE,
  BUCKET_FILTERS,
  BUCKET_LABELS,
  QUEUE_SORT_OPTIONS,
  CLOSED_INTAKE_STATUSES,
  RUN_REMAINING_STOP_STAGES,
  ACTIVE_PREVIEW_REFRESH_INTERVAL_MS,
  SORT_FIELDS,
  FILTER_LABELS,
  STATIC_FILTER_OPTIONS,
  titleize,
  getDecisionText,
  formatTableDate,
  normalizeOptions,
  optionList,
  getOptionLabel,
  getSelectedOptionLabels,
  getStatusClass,
  getStageClass,
  getSortIconClass,
  getDefaultSortOrder,
  getSortDirectionLabel,
  getPipedriveText,
  getCellClassName,
  getCellText,
  hasLeadActivePipelineWork,
  applyRunRemainingResultState,
  buildCsv,
  downloadCsv,
  readJson,
  writeJson,
  readTableState,
  isActiveFilterValue,
  serializeFilterParam,
  getLeadLabel,
  getDefendantLabel,
  getStateLabel,
  getNextActionLabel,
  getLeadMetaLine,
  getDocumentLabel,
  getLeadFlagItems,
  needsStaffReviewLead,
  groupLeadsByInbox,
  groupLeadsByStage,
  getPipelineVersionLabel,
  getLatestRun,
  buildPreviewPipelineStages,
  getPreviewCurrentStage,
  hasPreviewActivePipelineWork
} from './Queue/intakeQueueUtils';



function IntakeQueue() {
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);
  const { count: assignedReviewCount } = useAssignedAttorneyReviewCount();
  const [leads, setLeads] = useState([]);
  const [boardLeads, setBoardLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [hasLoadedLeads, setHasLoadedLeads] = useState(false);
  const [tableState, setTableState] = useState(() => readTableState());
  const { filters, page, pageSize, sortField, sortOrder } = tableState;
  const [error, setError] = useState('');
  const [boardError, setBoardError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [runningPipelineLeadId, setRunningPipelineLeadId] = useState(null);
  const [runningStageLeadId, setRunningStageLeadId] = useState(null);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const queueView = DEFAULT_QUEUE_VIEW;
  const [selectedLeadId, setSelectedLeadId] = useState(() => readIntakeViewState('intake').selectedLeadId || null);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(

  () => {
    const storedVersion = localStorage.getItem(COLUMNS_VERSION_KEY);
    const stored = readJson(COLUMNS_KEY, DEFAULT_COLUMN_KEYS);
    const validKeys = Array.isArray(stored) ?
    stored.filter((key) => DEFAULT_COLUMN_KEYS.includes(key)) :
    [];
    const selectedKeys = validKeys.length > 0 ? validKeys : DEFAULT_COLUMN_KEYS;
    if (storedVersion !== COLUMNS_VERSION) {
      return DEFAULT_COLUMN_KEYS.filter((key) => selectedKeys.includes(key) || key === 'dedupe' || key === 'documents');
    }
    return selectedKeys;
  });
  const [options, setOptions] = useState({
    statuses: FALLBACK_STATUSES,
    stages: FALLBACK_STAGES,
    categories: [],
    states: [],
    sources: [],
    assigned_owners: [],
    final_decisions: []
  });

  useEffect(() => {
    let cancelled = false;
    fetchIntakeOptions()
    .then((response) => {
      if (cancelled) return;
      setOptions((prev) => ({
        ...prev,
        ...response.data,
        statuses: response.data.statuses || prev.statuses,
        stages: response.data.stages || prev.stages
      }));
    })
    .catch((error) => {
      console.error('Failed to load intake options:', error);
    });
    return () => {cancelled = true;};
  }, []);

  const buildParams = useCallback(

  () => {
    const params = {
      page,
      page_size: pageSize,
      sort: sortField,
      direction: sortOrder === 1 ? 'asc' : 'desc'
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (isActiveFilterValue(value)) {
        params[key] = serializeFilterParam(value);
      }
    });
    return params;
  }, [filters, page, pageSize, sortField, sortOrder]);

  const buildExportParams = useCallback(

  (exportPage) => {
    const params = {
      page: exportPage,
      page_size: 200,
      sort: sortField,
      direction: sortOrder === 1 ? 'asc' : 'desc'
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (isActiveFilterValue(value)) {
        params[key] = serializeFilterParam(value);
      }
    });
    return params;
  }, [filters, sortField, sortOrder]);

  const buildBoardParams = useCallback(

  (boardPage) => {
    const params = {
      page: boardPage,
      page_size: BOARD_PAGE_SIZE,
      sort: sortField,
      direction: sortOrder === 1 ? 'asc' : 'desc'
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (isActiveFilterValue(value)) {
        params[key] = serializeFilterParam(value);
      }
    });
    return params;
  }, [filters, sortField, sortOrder]);

  const loadLeads = useCallback(

  () => {
    setLoading(true);
    setError('');
    fetchIntakeLeads(buildParams())
    .then((response) => {
      setLeads(response.data.leads || []);
      setTotal(response.data.total || 0);
    })
    .catch((error) => {
      console.error('Failed to load intake leads:', error);
      setError('Unable to load intake leads.');
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load intake leads' });
    })
    .finally(() => {
      setHasLoadedLeads(true);
      setLoading(false);
    });
  }, [buildParams, showToast]);

  const loadBoardLeads = useCallback(

  async () => {
    setBoardLoading(true);
    setBoardError('');
    try {
      const firstResponse = await fetchIntakeLeads(buildBoardParams(1));
      const firstPayload = firstResponse.data || {};
      const pageCount = Number(firstPayload.pages || 1);
      const remainingPages = Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) => index + 2);
      const remainingResponses = await Promise.all(
        remainingPages.map((boardPage) => fetchIntakeLeads(buildBoardParams(boardPage)))
      );
      const allRows = [
        ...(firstPayload.leads || []),
        ...remainingResponses.flatMap((response) => response.data?.leads || [])
      ];
      setBoardLeads(allRows);
    } catch (error) {
      console.error('Failed to load intake board leads:', error);
      setBoardError('Unable to load intake board.');
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load intake board' });
    } finally {
      setBoardLoading(false);
    }
  }, [buildBoardParams, showToast]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    if (queueView !== 'board') return undefined;
    loadBoardLeads();
    return undefined;
  }, [loadBoardLeads, queueView]);

  useEffect(

  () => {
    const activeRows = queueView === 'board' ? boardLeads : leads;
    const hasActiveProcessingRows = activeRows.some((lead) => ['processing', 'error'].includes(lead.status) || hasLeadActivePipelineWork(lead));
    if (!hasActiveProcessingRows) return undefined;
    const interval = setInterval(() => {
      loadLeads();
      if (queueView === 'board') {
        loadBoardLeads();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [boardLeads, leads, loadBoardLeads, loadLeads, queueView]);

  useEffect(() => {
    writeJson(COLUMNS_KEY, visibleColumnKeys);
    try {
      localStorage.setItem(COLUMNS_VERSION_KEY, COLUMNS_VERSION);
    } catch (error) {
      console.warn(`Failed to persist ${COLUMNS_VERSION_KEY}:`, error);
    }
  }, [visibleColumnKeys]);

  useEffect(() => {
    writeJson(TABLE_STATE_KEY, tableState);
    try {
      localStorage.setItem(TABLE_STATE_VERSION_KEY, TABLE_STATE_VERSION);
    } catch (error) {
      console.warn(`Failed to persist ${TABLE_STATE_VERSION_KEY}:`, error);
    }
  }, [tableState]);

  useEffect(() => {
    writeIntakeViewState('intake', { selectedLeadId });
  }, [selectedLeadId]);

  useEffect(

  () => {
    if (!selectedLeadId) {
      setPreviewPayload(null);
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    fetchIntakeLeadPreview(selectedLeadId)
    .then((response) => {
      if (!cancelled) {
        setPreviewPayload(response.data);
        setPreviewError('');
      }
    })
    .catch((error) => {
      console.error('Failed to load intake lead preview:', error);
      if (!cancelled) {
        setPreviewPayload(null);
        setPreviewError('Unable to load lead preview.');
      }
    })
    .finally(() => {
      if (!cancelled) setPreviewLoading(false);
    });
    return () => {cancelled = true;};
  }, [selectedLeadId]);

  const visibleColumns = useMemo(() => {
    const selectedColumns = INTAKE_COLUMNS.filter((column) => visibleColumnKeys.includes(column.key));
    return selectedColumns.length > 0 ? selectedColumns : INTAKE_COLUMNS;
  }, [visibleColumnKeys]);

  const updateFilter =

  (key, value) => {
    setTableState((current) => ({
      ...current,
      filters: { ...current.filters, [key]: value },
      page: 1
    }));
  };

  const clearFilters =

  () => {
    setTableState((current) => ({
      ...current,
      filters: { ...DEFAULT_FILTERS },
      page: 1
    }));
  };

  const setBucketFilter =

  (bucket) => {
    setTableState((current) => ({
      ...current,
      filters: { ...current.filters, bucket },
      page: 1
    }));
  };

  const removeFilter =

  (key) => {
    setTableState((current) => ({
      ...current,
      filters: { ...current.filters, [key]: DEFAULT_FILTERS[key] },
      page: 1
    }));
  };

  const toggleColumn =

  (columnKey) => {
    setVisibleColumnKeys((current) => {
      if (current.includes(columnKey)) {
        return current.filter((key) => key !== columnKey);
      }
      return current.concat(columnKey);
    });
  };

  const handleSort =

  (field) => {
    if (!field) return;
    setTableState((current) => ({
      ...current,
      sortField: field,
      sortOrder: current.sortField === field && current.sortOrder === 1 ? -1 : 1,
      page: 1
    }));
  };

  const updateSortField =

  (field) => {
    if (!SORT_FIELDS.has(field)) return;
    setTableState((current) => ({
      ...current,
      sortField: field,
      sortOrder: current.sortField === field ? current.sortOrder : getDefaultSortOrder(field),
      page: 1
    }));
  };

  const toggleSortOrder =

  () => {
    setTableState((current) => ({
      ...current,
      sortOrder: current.sortOrder === 1 ? -1 : 1,
      page: 1
    }));
  };

  const filterOptions = useMemo(() => ({
    statuses: normalizeOptions(options.statuses || FALLBACK_STATUSES),
    stages: normalizeOptions(options.stages || FALLBACK_STAGES),
    categories: optionList(options.categories),
    states: optionList(options.states),
    sources: optionList(options.sources),
    assigned_owners: optionList(options.assigned_owners),
    final_decisions: optionList(options.final_decisions),
    staff_review_required: STATIC_FILTER_OPTIONS.staff_review_required,
    potential_class_action: STATIC_FILTER_OPTIONS.potential_class_action,
    outreach_email_sent: STATIC_FILTER_OPTIONS.outreach_email_sent,
    dedupe: STATIC_FILTER_OPTIONS.dedupe
  }), [options]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const firstRow = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastRow = Math.min(currentPage * pageSize, total);

  useEffect(() => {
    if (hasLoadedLeads && page !== currentPage) {
      setTableState((current) => ({ ...current, page: currentPage }));
    }
  }, [currentPage, hasLoadedLeads, page]);

  const activeFilterEntries = Object.entries(filters)
  .filter(([, value]) => isActiveFilterValue(value));

  const getFilterChipValue =

  (key, value) => {
    if (key === 'bucket') return BUCKET_LABELS[value] || titleize(value);
    if (key === 'status') return getSelectedOptionLabels(options.statuses || FALLBACK_STATUSES, value);
    if (key === 'stage') return getSelectedOptionLabels(options.stages || FALLBACK_STAGES, value);
    if (key === 'category') return getOptionLabel(options.categories, value);
    if (key === 'state') return getOptionLabel(options.states, value);
    if (key === 'source') return getOptionLabel(options.sources, value);
    if (key === 'assigned_owner') return getOptionLabel(options.assigned_owners, value);
    if (key === 'final_decision') return getOptionLabel(options.final_decisions, value);
    if (key === 'dedupe') return STATIC_FILTER_OPTIONS.dedupe.find((option) => option.value === value)?.label || value;
    if (key === 'staff_review_required') return STATIC_FILTER_OPTIONS.staff_review_required.find((option) => option.value === value)?.label || value;
    if (key === 'potential_class_action') return STATIC_FILTER_OPTIONS.potential_class_action.find((option) => option.value === value)?.label || value;
    if (key === 'outreach_email_sent') return STATIC_FILTER_OPTIONS.outreach_email_sent.find((option) => option.value === value)?.label || value;
    return value;
  };

  const exportCsv =

  async () => {
    setExporting(true);
    try {
      let exportPage = 1;
      let pageCount = 1;
      const allRows = [];

      do {
        const response = await fetchIntakeLeads(buildExportParams(exportPage));
        allRows.push(...(response.data.leads || []));
        pageCount = response.data.pages || pageCount;
        exportPage += 1;
      } while (exportPage <= pageCount);

      downloadCsv(buildCsv(allRows, visibleColumns));
    } catch (error) {
      console.error('Failed to export intake leads:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to export intake leads' });
    } finally {
      setExporting(false);
    }
  };

  const latestRunAllResult =

  (result) => {
    let latest = result || {};
    let guard = 0;
    while (latest?.run_result?.manual_pipeline_continuation && guard < 20) {
      latest = latest.run_result.manual_pipeline_continuation;
      guard += 1;
    }
    return latest || result || {};
  };

  const aiProcessingLockReason =

  (lead) => lead?.ai_processing_locked ?
    lead.ai_processing_lock_reason || 'AI processing is locked for this lead.' :
    '';

  const runRemainingDisabledReason =

  (lead) => {
    if (!lead?.id) return 'Lead is not available.';
    const lockReason = aiProcessingLockReason(lead);
    if (lockReason) return lockReason;
    if (hasLeadActivePipelineWork(lead)) return 'Pipeline stages are already running.';
    if (CLOSED_INTAKE_STATUSES.has(lead.status)) return 'Lead is closed.';
    if (!lead.current_stage) return 'No current pipeline stage is available.';
    if (RUN_REMAINING_STOP_STAGES.has(lead.current_stage)) return 'Pipeline is waiting for a manual decision or conversion step.';
    return '';
  };

  const runNextStageDisabledReason =

  (lead, payload) => {
    if (!lead?.id) return 'Lead is not available.';
    if (runningStageLeadId || runningPipelineLeadId) return 'Another pipeline action is running.';
    const lockReason = aiProcessingLockReason(lead);
    if (lockReason) return lockReason;
    if (hasPreviewActivePipelineWork(payload, lead)) return 'Pipeline stages are already running.';
    if (CLOSED_INTAKE_STATUSES.has(lead.status)) return 'Lead is closed.';
    const currentStage = getPreviewCurrentStage(payload, lead);
    if (!currentStage?.stage?.key) return 'No current pipeline stage is available.';
    if (RUN_REMAINING_STOP_STAGES.has(currentStage.stage.key)) return 'Pipeline is waiting for a manual decision or conversion step.';
    return '';
  };

  const updateLeadRow = useCallback(

  (updatedLead) => {
    if (!updatedLead?.id) return;
    setLeads((current) =>
    current.map((row) => row.id === updatedLead.id ? { ...row, ...updatedLead } : row)
    );
    setBoardLeads((current) =>
    current.map((row) => row.id === updatedLead.id ? { ...row, ...updatedLead } : row)
    );
    setPreviewPayload((current) => {
      if (!current?.lead || current.lead.id !== updatedLead.id) return current;
      return { ...current, lead: { ...current.lead, ...updatedLead } };
    });
  }, []);

  useEffect(

  () => {
    if (!selectedLeadId || !previewPayload || previewLoading) return undefined;
    const previewLead = previewPayload.lead;
    if (previewLead?.id && String(previewLead.id) !== String(selectedLeadId)) return undefined;
    if (!hasPreviewActivePipelineWork(previewPayload, previewLead)) return undefined;

    let cancelled = false;
    const refreshActivePreview =

    async () => {
      try {
        const response = await fetchIntakeLeadPreview(selectedLeadId);
        if (cancelled) return;
        setPreviewPayload((current) => {
          const currentLeadId = current?.lead?.id || selectedLeadId;
          if (String(currentLeadId) !== String(selectedLeadId)) return current;
          return response.data;
        });
        if (response.data?.lead) {
          updateLeadRow(response.data.lead);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to refresh active intake lead preview:', error);
        }
      }
    };

    const interval = setInterval(refreshActivePreview, ACTIVE_PREVIEW_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [previewLoading, previewPayload, selectedLeadId, updateLeadRow]);

  const runRemainingForLead =

  async (event, lead) => {
    event?.stopPropagation?.();
    const disabledReason = runRemainingDisabledReason(lead);
    if (disabledReason || runningPipelineLeadId || runningStageLeadId) return;
    setRunningPipelineLeadId(lead.id);
    try {
      const response = await runIntakePipeline(lead.id, { reason: 'Manual run remaining pipeline from intake queue' });
      const result = latestRunAllResult(response?.data?.run_all_result);
      if (response?.data?.lead) {
        updateLeadRow(applyRunRemainingResultState(response.data.lead, result));
      } else {
        loadLeads();
        if (queueView === 'board') {
          loadBoardLeads();
        }
      }
      const detail = result.message || 'Run remaining pipeline requested.';
      if (result.status === 'blocked') {
        showToast({ severity: 'warn', summary: 'Pipeline Paused', detail });
      } else if (result.status === 'completed' || result.status === 'stopped') {
        showToast({ severity: 'success', summary: 'Pipeline Stopped', detail });
      } else if (result.status === 'waiting') {
        showToast({ severity: 'info', summary: 'Pipeline Armed', detail });
      } else {
        showToast({ severity: 'info', summary: 'Pipeline Running', detail });
      }
    } catch (error) {
      console.error('Failed to run remaining intake pipeline:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run remaining pipeline' });
    } finally {
      setRunningPipelineLeadId(null);
    }
  };

  const runNextStageForLead =

  async (event, lead, payload) => {
    event?.stopPropagation?.();
    const disabledReason = runNextStageDisabledReason(lead, payload);
    if (disabledReason) return;
    const currentStage = getPreviewCurrentStage(payload, lead);
    const stage = currentStage.stage;
    setRunningStageLeadId(lead.id);
    try {
      const response = await runIntakeStage(lead.id, {
        stage_key: stage.key,
        reason: `Manual run next stage from intake queue: ${stage.label || stage.key}`
      });
      if (response?.data?.lead) {
        updateLeadRow(response.data.lead);
      }
      const detailResponse = await fetchIntakeLeadPreview(lead.id);
      if (String(selectedLeadId || '') === String(lead.id)) {
        setPreviewPayload(detailResponse.data);
      }
      const status = String(response?.data?.run_result?.status || response?.data?.stage_run?.status || '').toLowerCase();
      const message = response?.data?.run_result?.message || response?.data?.stage_run?.error || `${stage.label || 'Stage'} run requested.`;
      if (FAILED_RUN_STATUSES.has(status)) {
        showToast({ severity: 'error', summary: 'Stage Failed', detail: message });
      } else if (REVIEW_RUN_STATUSES.has(status)) {
        showToast({ severity: 'warn', summary: 'Review Needed', detail: message });
      } else if (ACTIVE_STATUSES.has(status)) {
        showToast({ severity: 'info', summary: 'Stage Running', detail: message });
      } else if (COMPLETE_STATUSES.has(status)) {
        showToast({ severity: 'success', summary: 'Stage Complete', detail: message });
      } else if (status === 'skipped' || status === 'no_action') {
        showToast({ severity: 'warn', summary: 'No Action', detail: message });
      } else {
        showToast({ severity: 'info', summary: 'Stage Requested', detail: message });
      }
    } catch (error) {
      console.error('Failed to run next intake stage:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run next stage' });
    } finally {
      setRunningStageLeadId(null);
    }
  };

  const refreshCurrentView =

  () => {
    loadLeads();
    if (queueView === 'board') {
      loadBoardLeads();
    }
  };
  const currentBucket = filters.bucket || DEFAULT_FILTERS.bucket;
  const selectedLead = (queueView === 'board' ? boardLeads : leads).find((lead) => String(lead.id) === String(selectedLeadId)) ||
  leads.find((lead) => String(lead.id) === String(selectedLeadId)) ||
  boardLeads.find((lead) => String(lead.id) === String(selectedLeadId)) ||
  null;
  const visiblePreviewLead = previewPayload ? selectedLead : (previewLoading ? null : selectedLead);
  const queueBodyProps = {
    leads,
    loading,
    selectedLeadId,
    runningPipelineLeadId,
    onSelectLead: (lead) => setSelectedLeadId(lead.id),
    onRunRemaining: runRemainingForLead,
    runRemainingDisabledReason,
  };
  const preview = (
    <IntakeLeadPreview
      lead={visiblePreviewLead}
      payload={previewPayload}
      loading={previewLoading}
      error={previewError}
      runningPipelineLeadId={runningPipelineLeadId}
      runningStageLeadId={runningStageLeadId}
      onRunNextStage={runNextStageForLead}
      onRunRemaining={runRemainingForLead}
      runNextStageDisabledReason={runNextStageDisabledReason}
      runRemainingDisabledReason={runRemainingDisabledReason} />
  );
  const renderSelectedView =

  () => {
    if (queueView === 'table') {
      return (
        <IntakeFullTable
          leads={leads}
          loading={loading}
          visibleColumns={visibleColumns}
          filters={filters}
          filterOptions={filterOptions}
          sortField={sortField}
          sortOrder={sortOrder}
          runningPipelineLeadId={runningPipelineLeadId}
          onSort={handleSort}
          onFilterChange={updateFilter}
          onRunRemaining={runRemainingForLead}
          runRemainingDisabledReason={runRemainingDisabledReason}
          onOpenLead={(lead) => navigate(`/intake/leads/${lead.id}`)} />
      );
    }
    if (queueView === 'inbox') {
      return <IntakeSplitQueue main={<IntakePriorityInbox {...queueBodyProps} />} preview={preview} previewLoading={previewLoading} />;
    }
    if (queueView === 'board') {
      return (
        <IntakeSplitQueue
          main={<IntakeStageBoard {...queueBodyProps} leads={boardLeads} loading={boardLoading} />}
          preview={preview}
          previewLoading={previewLoading} />
      );
    }
    if (queueView === 'summary_table') {
      return <IntakeSplitQueue main={<IntakeSummaryTable {...queueBodyProps} />} preview={preview} previewLoading={previewLoading} />;
    }
    return <IntakeSplitQueue main={<IntakeWorklist {...queueBodyProps} />} preview={preview} previewLoading={previewLoading} />;
  };

  return (
    <div className="intake-queue">
            <div className="intake-toolbar">
                <div className="intake-toolbar__primary">
                    <div className="intake-title">
                        <IntakeViewSwitch activeView="intake" assignedReviewCount={assignedReviewCount} />
                        <span className="intake-title__count">{total.toLocaleString()} leads</span>
                    </div>
                    <label className="intake-search">
                        <span className="intake-sr-only">Search intake leads</span>
                        <input
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder="Search leads"
              aria-label="Search intake leads" />

                    </label>
                </div>
                {queueView === 'board' ?
        <div className="intake-paginator intake-paginator--board" aria-label="Intake board count">
                    <span className="intake-paginator__range">All {total.toLocaleString()} matching leads</span>
                </div> :
        <div className="intake-paginator" aria-label="Intake pagination">
                    <label className="intake-paginator__rows">
                        Rows
                        <select
              value={pageSize}
              onChange={(event) => {
                setTableState((current) => ({
                  ...current,
                  pageSize: Number(event.target.value),
                  page: 1
                }));
              }}>

                            {ROWS_PER_PAGE_OPTIONS.map((option) =>
              <option key={option} value={option}>{option}</option>
              )}
                        </select>
                    </label>
                    <span className="intake-paginator__range">{firstRow}-{lastRow} of {total}</span>
                    <div className="intake-paginator__buttons">
                        <button
              type="button"
              className="intake-icon-button"
              onClick={() => setTableState((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
              disabled={currentPage <= 1}
              aria-label="Previous page">

                            <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
                        </button>
                        <button
              type="button"
              className="intake-icon-button"
              onClick={() => setTableState((current) => ({ ...current, page: Math.min(totalPages, current.page + 1) }))}
              disabled={currentPage >= totalPages}
              aria-label="Next page">

                            <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
                        </button>
                    </div>
                </div>}
                <div className="intake-toolbar__actions">
                    {queueView === 'table' &&
                    <div className="intake-menu-wrap">
                        <button
              type="button"
              className="intake-button"
              onClick={() => setColumnMenuOpen((open) => !open)}
              aria-expanded={columnMenuOpen}
              aria-label="Columns">

                            <AppIcon icon="pi pi-table" aria-hidden="true" />
                            <span>Columns</span>
                        </button>
                        {columnMenuOpen &&
            <div className="intake-popover intake-column-menu" role="dialog" aria-label="Select intake columns">
                                <div className="intake-popover__title">Select columns</div>
                                {INTAKE_COLUMNS.map((column) =>
              <label key={column.key} className="intake-checkbox-row">
                                        <input
                  type="checkbox"
                  checked={visibleColumnKeys.includes(column.key)}
                  onChange={() => toggleColumn(column.key)} />

                                        <span>{column.label}</span>
                                    </label>
              )}
                            </div>}

                    </div>
                    }
                    <button type="button" className="intake-button" onClick={exportCsv} disabled={exporting} aria-label="Export CSV">
                        <AppIcon icon="pi pi-download" aria-hidden="true" />
                        <span>{exporting ? 'Exporting' : 'Export CSV'}</span>
                    </button>
                    <button type="button" className="intake-button" onClick={clearFilters} aria-label="Clear">
                        <AppIcon icon="pi pi-filter-slash" aria-hidden="true" />
                        <span>Clear</span>
                    </button>
                    <button type="button" className="intake-button" onClick={refreshCurrentView} aria-label="Refresh">
                        <AppIcon icon="pi pi-refresh" aria-hidden="true" />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            <div className="intake-bucket-bar" aria-label="Saved intake views">
                {BUCKET_FILTERS.map((bucket) =>
        <button
          type="button"
          key={bucket.key}
          className={`intake-bucket-chip${currentBucket === bucket.key ? ' intake-bucket-chip--active' : ''}`}
          aria-pressed={currentBucket === bucket.key}
          onClick={() => setBucketFilter(bucket.key)}>
                    <AppIcon icon={bucket.icon} aria-hidden="true" />
                    <span>{bucket.label}</span>
                </button>
        )}
            </div>

            <IntakeQueueFilterBar
              filters={filters}
              filterOptions={filterOptions}
              sortField={sortField}
              sortOrder={sortOrder}
              onFilterChange={updateFilter}
              onSortFieldChange={updateSortField}
              onSortOrderToggle={toggleSortOrder} />

            <div className="intake-filter-chips" aria-label="Active filters">
                {activeFilterEntries.length === 0 &&
        <span className="intake-filter-chips__empty">No active filters</span>}

                {activeFilterEntries.map(([key, value]) =>
        <button
          type="button"
          className="intake-chip"
          key={key}
          onClick={() => removeFilter(key)}>

                        <span>{FILTER_LABELS[key] || key}: {getFilterChipValue(key, value)}</span>
                        <AppIcon icon="pi pi-times" aria-hidden="true" />
                    </button>
        )}
            </div>

            {(queueView === 'board' ? boardError || error : error) &&
      <div className="intake-inline-alert" role="alert">
                    <span>{queueView === 'board' ? boardError || error : error}</span>
                    <button type="button" className="intake-button" onClick={refreshCurrentView}>
                        Retry
                    </button>
                </div>}


            {renderSelectedView()}
        </div>);

}



function IntakeQueueFilterBar({
  filters,
  filterOptions,
  sortField,
  sortOrder,
  onFilterChange,
  onSortFieldChange,
  onSortOrderToggle
}) {
  const sortDirectionLabel = getSortDirectionLabel(sortOrder, sortField);
  return (
    <div className="intake-quick-filter-bar" aria-label="Intake queue filters and sorting">
            <div className="intake-quick-filter intake-quick-filter--wide">
                <span>Stage</span>
                <IntakeHeaderMultiSelectFilter
          label="Stage"
          values={filters.stage}
          options={filterOptions.stages}
          onChange={(nextValues) => onFilterChange('stage', nextValues)} />
            </div>
            <div className="intake-quick-filter intake-quick-filter--wide">
                <span>Status</span>
                <IntakeHeaderMultiSelectFilter
          label="Status"
          values={filters.status}
          options={filterOptions.statuses}
          onChange={(nextValues) => onFilterChange('status', nextValues)} />
            </div>
            <label className="intake-quick-filter">
                <span>Category</span>
                <select
          className="intake-column-filter-input"
          value={filters.category}
          onChange={(event) => onFilterChange('category', event.target.value)}
          aria-label="Filter Category">
                    {filterOptions.categories.map((option) =>
          <option key={option.value} value={option.value}>{option.label}</option>
          )}
                </select>
            </label>
            <label className="intake-quick-filter">
                <span>Owner</span>
                <select
          className="intake-column-filter-input"
          value={filters.assigned_owner}
          onChange={(event) => onFilterChange('assigned_owner', event.target.value)}
          aria-label="Filter Owner">
                    {filterOptions.assigned_owners.map((option) =>
          <option key={option.value} value={option.value}>{option.label}</option>
          )}
                </select>
            </label>
            <label className="intake-quick-filter">
                <span>Review</span>
                <select
          className="intake-column-filter-input"
          value={filters.staff_review_required}
          onChange={(event) => onFilterChange('staff_review_required', event.target.value)}
          aria-label="Filter Staff Review">
                    {filterOptions.staff_review_required.map((option) =>
          <option key={option.value} value={option.value}>{option.label}</option>
          )}
                </select>
            </label>
            <label className="intake-quick-filter">
                <span>Emailed</span>
                <select
          className="intake-column-filter-input"
          value={filters.outreach_email_sent}
          onChange={(event) => onFilterChange('outreach_email_sent', event.target.value)}
          aria-label="Filter Emailed">
                    {filterOptions.outreach_email_sent.map((option) =>
          <option key={option.value} value={option.value}>{option.label}</option>
          )}
                </select>
            </label>
            <label className="intake-quick-filter intake-quick-filter--date">
                <span>Created From</span>
                <input
          className="intake-column-filter-input"
          type="date"
          value={filters.created_start}
          onChange={(event) => onFilterChange('created_start', event.target.value)}
          aria-label="Filter Created From" />
            </label>
            <label className="intake-quick-filter intake-quick-filter--date">
                <span>Created To</span>
                <input
          className="intake-column-filter-input"
          type="date"
          value={filters.created_end}
          onChange={(event) => onFilterChange('created_end', event.target.value)}
          aria-label="Filter Created To" />
            </label>
            <label className="intake-quick-filter intake-quick-filter--sort">
                <span>Sort</span>
                <select
          className="intake-column-filter-input"
          value={sortField}
          onChange={(event) => onSortFieldChange(event.target.value)}
          aria-label="Sort intake leads">
                    {QUEUE_SORT_OPTIONS.map((option) =>
          <option key={option.value} value={option.value}>{option.label}</option>
          )}
                </select>
            </label>
            <button
        type="button"
        className="intake-button intake-sort-direction-button"
        onClick={onSortOrderToggle}
        aria-label={`Sort ${sortOrder === -1 ? 'ascending' : 'descending'}`}>
                <AppIcon icon={sortOrder === -1 ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'} aria-hidden="true" />
                <span>{sortDirectionLabel}</span>
            </button>
        </div>);

}



function IntakeSplitQueue({ main, preview, previewLoading = false }) {
  const [previewPulseActive, setPreviewPulseActive] = useState(previewLoading);
  const [previewPulseFinishing, setPreviewPulseFinishing] = useState(false);

  useEffect(() => {
    if (previewLoading) {
      setPreviewPulseActive(true);
      setPreviewPulseFinishing(false);
    } else if (previewPulseActive) {
      setPreviewPulseFinishing(true);
    }
  }, [previewLoading, previewPulseActive]);

  const finishPreviewPulse =

  (event) => {
    if (event.target !== event.currentTarget || !previewPulseFinishing) return;
    setPreviewPulseActive(false);
    setPreviewPulseFinishing(false);
  };

  const previewClassName = `intake-queue-preview${previewPulseActive ? ' intake-queue-preview--loading' : ''}`;
  return (
    <div className="intake-queue-split">
            <div className="intake-queue-main">{main}</div>
            <aside
              className={previewClassName}
              aria-label="Selected lead preview"
              aria-busy={previewLoading ? 'true' : 'false'}
              onAnimationIteration={finishPreviewPulse}>
              {preview}
            </aside>
        </div>);

}



export function IntakeRunButton({ lead, runningPipelineLeadId, onRunRemaining, runRemainingDisabledReason, pipelineStages = [], compact = false, table = false }) {
  // A resolved lead remains visible as Intake history, but it is never a manual
  // AI-work item.  Keeping the action out of the row avoids suggesting that a
  // rejected, archived, duplicate, or converted record can be resumed here.
  if (CLOSED_INTAKE_STATUSES.has(lead?.status)) return null;
  const rowRunning = runningPipelineLeadId === lead.id || hasLeadActivePipelineWork(lead);
  const aiLocked = Boolean(lead?.ai_processing_locked);
  const disabledReason = runRemainingDisabledReason(lead);
  const actionLabel = getRunPipelineActionLabel({
    currentStageKey: lead?.current_stage,
    pipelineStages: pipelineStages.length ? pipelineStages : lead?.pipeline_stages,
    running: rowRunning,
    compact
  });
  const actionTitle = getRunPipelineActionTitle({
    currentStageKey: lead?.current_stage,
    pipelineStages: pipelineStages.length ? pipelineStages : lead?.pipeline_stages,
    disabledReason
  });
  return (
    <button
      type="button"
      className={`intake-button intake-run-remaining-button${compact ? ' intake-run-remaining-button--compact' : ''}${table ? ' intake-table-run-button' : ''}`}
      disabled={Boolean(disabledReason) || Boolean(runningPipelineLeadId)}
      title={actionTitle}
      aria-label={`${actionLabel} for ${getLeadLabel(lead)}`}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => onRunRemaining(event, lead)}>
            <AppIcon icon={rowRunning ? 'pi pi-spin pi-spinner' : aiLocked ? 'pi pi-lock' : 'pi pi-forward'} aria-hidden="true" />
            <span>{actionLabel}</span>
        </button>);

}



function IntakeLeadFlags({ lead, limit = null }) {
  const items = getLeadFlagItems(lead);
  const visibleItems = limit ? items.slice(0, limit) : items;
  return (
    <div className="intake-lead-flags" aria-label={`${getLeadLabel(lead)} flags`}>
            {visibleItems.map((item) =>
        <span key={item.key} className={`intake-badge ${item.className}`}>{item.label}</span>
        )}
        </div>);

}



function IntakeWorklistRow({ lead, selected, runningPipelineLeadId, onSelectLead, onRunRemaining, runRemainingDisabledReason }) {
  return (
    <article
      className={`intake-worklist-row${selected ? ' intake-worklist-row--selected' : ''}`}
      data-testid="intake-worklist-row"
      tabIndex={0}
      onClick={() => onSelectLead(lead)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectLead(lead);
        }
      }}>
            <div className="intake-worklist-row__lead">
                <strong>{getLeadLabel(lead)}</strong>
                <span>{getDefendantLabel(lead)}</span>
                <small>{getLeadMetaLine(lead)}</small>
            </div>
            <div className="intake-worklist-row__work">
                <span>{getNextActionLabel(lead)}</span>
                <small>{lead.next_action_at ? `Due ${formatDate(lead.next_action_at)}` : `Updated ${formatDate(lead.updated_at)}`}</small>
            </div>
            <IntakeLeadFlags lead={lead} limit={6} />
            <div className="intake-worklist-row__actions">
                <IntakeRunButton
          lead={lead}
          runningPipelineLeadId={runningPipelineLeadId}
          onRunRemaining={onRunRemaining}
          runRemainingDisabledReason={runRemainingDisabledReason} />
            </div>
        </article>);

}



function IntakeListEmptyState({ loading, leads }) {
  if (loading && leads.length === 0) {
    return <div className="intake-empty-state intake-queue-empty">Loading intake leads...</div>;
  }
  if (!loading && leads.length === 0) {
    return <div className="intake-empty-state intake-queue-empty">No intake leads found.</div>;
  }
  return null;
}



function IntakeWorklist({ leads, loading, selectedLeadId, runningPipelineLeadId, onSelectLead, onRunRemaining, runRemainingDisabledReason }) {
  const emptyState = IntakeListEmptyState({ loading, leads });
  if (emptyState) return emptyState;
  const staffReviewLeads = leads.filter(needsStaffReviewLead);
  const otherLeads = leads.filter((lead) => !needsStaffReviewLead(lead));
  const sections = [
    staffReviewLeads.length > 0 ? { key: 'staff-review', title: 'Needs Staff Review', leads: staffReviewLeads } : null,
    otherLeads.length > 0 ? { key: 'other', title: staffReviewLeads.length > 0 ? 'Other Leads' : '', leads: otherLeads } : null
  ].filter(Boolean);
  return (
    <div className="intake-worklist" data-testid="intake-worklist">
            {sections.map((section) =>
        <section className="intake-worklist-section" key={section.key} aria-label={section.title || 'Intake leads'}>
                    {section.title &&
          <div className="intake-worklist-section__header">
                        <h2>{section.title}</h2>
                        <span>{section.leads.length}</span>
                    </div>}
                    <div className="intake-worklist-section__rows">
                        {section.leads.map((lead) =>
              <IntakeWorklistRow
                key={lead.id}
                lead={lead}
                selected={String(selectedLeadId || '') === String(lead.id)}
                runningPipelineLeadId={runningPipelineLeadId}
                onSelectLead={onSelectLead}
                onRunRemaining={onRunRemaining}
                runRemainingDisabledReason={runRemainingDisabledReason} />
              )}
                    </div>
                </section>
        )}
        </div>);

}



function IntakePriorityInbox({ leads, loading, selectedLeadId, runningPipelineLeadId, onSelectLead, onRunRemaining, runRemainingDisabledReason }) {
  const emptyState = IntakeListEmptyState({ loading, leads });
  if (emptyState) return emptyState;
  const groups = groupLeadsByInbox(leads).filter((group) => group.leads.length > 0);
  return (
    <div className="intake-priority-inbox" data-testid="intake-priority-inbox">
            {groups.map((group) =>
        <section className="intake-priority-group" key={group.key}>
                    <div className="intake-priority-group__header">
                        <h2>{group.label}</h2>
                        <span>{group.leads.length}</span>
                    </div>
                    <div className="intake-priority-group__rows">
                        {group.leads.map((lead) =>
              <IntakeWorklistRow
                key={lead.id}
                lead={lead}
                selected={String(selectedLeadId || '') === String(lead.id)}
                runningPipelineLeadId={runningPipelineLeadId}
                onSelectLead={onSelectLead}
                onRunRemaining={onRunRemaining}
                runRemainingDisabledReason={runRemainingDisabledReason} />
              )}
                    </div>
                </section>
        )}
        </div>);

}



function IntakeStageBoard({ leads, loading, selectedLeadId, onSelectLead }) {
  const emptyState = IntakeListEmptyState({ loading, leads });
  if (emptyState) return emptyState;
  const groups = groupLeadsByStage(leads);
  return (
    <div className="intake-stage-board" data-testid="intake-stage-board">
            {groups.map((group) =>
        <section className="intake-stage-column" key={group.key} aria-label={`${group.label} stage`}>
                    <div className="intake-stage-column__header">
                        <h2>{group.label}</h2>
                        <span>{group.leads.length}</span>
                    </div>
                    <div className="intake-stage-column__cards">
                        {group.leads.map((lead) => {
            const selected = String(selectedLeadId || '') === String(lead.id);
            return (
              <article
                key={lead.id}
                className={`intake-stage-card${selected ? ' intake-stage-card--selected' : ''}`}
                tabIndex={0}
                onClick={() => onSelectLead(lead)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectLead(lead);
                  }
                }}>
                                <strong>{getLeadLabel(lead)}</strong>
                                <span>{getDefendantLabel(lead)}</span>
                                <small>{getNextActionLabel(lead)}</small>
                                <IntakeLeadFlags lead={lead} limit={4} />
                            </article>);
          })}
                    </div>
                </section>
        )}
        </div>);

}



function IntakeSummaryTable({ leads, loading, selectedLeadId, runningPipelineLeadId, onSelectLead, onRunRemaining, runRemainingDisabledReason }) {
  return (
    <div className="intake-summary-table-wrap" data-testid="intake-summary-table">
            <table className="intake-summary-table">
                <thead>
                    <tr>
                        <th>Lead</th>
                        <th>Next Work</th>
                        <th>Pipeline</th>
                        <th>Flags</th>
                        <th>Owner</th>
                        <th>Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && leads.length === 0 &&
          <tr><td colSpan="6" className="intake-empty">Loading intake leads...</td></tr>}
                    {!loading && leads.length === 0 &&
          <tr><td colSpan="6" className="intake-empty">No intake leads found.</td></tr>}
                    {leads.map((lead) => {
          const selected = String(selectedLeadId || '') === String(lead.id);
          return (
            <tr
              key={lead.id}
              className={selected ? 'intake-summary-table__row--selected' : ''}
              tabIndex={0}
              onClick={() => onSelectLead(lead)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectLead(lead);
                }
              }}>
                            <td>
                                <div className="intake-summary-cell">
                                    <strong>{getLeadLabel(lead)}</strong>
                                    <span>{getDefendantLabel(lead)}</span>
                                </div>
                            </td>
                            <td>
                                <div className="intake-summary-cell">
                                    <strong>{getNextActionLabel(lead)}</strong>
                                    <span>{lead.next_action_at ? formatDate(lead.next_action_at) : '-'}</span>
                                </div>
                            </td>
                            <td>
                                <IntakeLeadFlags lead={lead} limit={2} />
                            </td>
                            <td>
                                <IntakeLeadFlags lead={lead} limit={6} />
                            </td>
                            <td>{lead.assigned_owner_email || '-'}</td>
                            <td>
                                <div className="intake-summary-table__updated">
                                    <span>{formatTableDate(lead.updated_at)}</span>
                                    <IntakeRunButton
                    lead={lead}
                    compact
                    runningPipelineLeadId={runningPipelineLeadId}
                    onRunRemaining={onRunRemaining}
                    runRemainingDisabledReason={runRemainingDisabledReason} />
                                </div>
                            </td>
                        </tr>);
        })}
                </tbody>
            </table>
        </div>);

}



export function IntakeLeadPreview({
  lead,
  payload,
  loading,
  error,
  runningPipelineLeadId,
  runningStageLeadId,
  onRunNextStage,
  onRunRemaining,
  runNextStageDisabledReason,
  runRemainingDisabledReason,
  readOnly = false,
  batchResolutionLabel = '',
}) {
  const previewLead = payload?.lead || lead;
  if (!previewLead) {
    return (
      <div className="intake-preview-empty">
            <AppIcon icon="pi pi-arrow-left" aria-hidden="true" />
            <strong>Select a lead</strong>
        </div>);
  }
  const nextStage = getPreviewCurrentStage(payload, previewLead);
  const previewPipelineStages = payload?.pipeline_preview?.pipeline_stages || payload?.pipeline_stages || [];
  const nextStageDisabledReason = runNextStageDisabledReason(previewLead, payload);
  const nextStageRunning = runningStageLeadId === previewLead.id;
  const nextStageAiLocked = Boolean(previewLead?.ai_processing_locked);
  return (
    <div className="intake-preview-panel" data-testid="intake-preview-panel">
            <div className="intake-preview-panel__header">
                <div>
                    <span>Lead #{previewLead.id}</span>
                    <h2>{getLeadLabel(previewLead)}</h2>
                    <p>{getDefendantLabel(previewLead)}</p>
                </div>
                <Link to={`/intake/leads/${previewLead.id}`} className="intake-icon-button" aria-label="Open full detail">
                    <AppIcon icon="pi pi-external-link" aria-hidden="true" />
                </Link>
            </div>
            {error && <div className="intake-inline-alert" role="alert"><span>{error}</span></div>}
            {!readOnly && <IntakeLeadFlags lead={previewLead} />}
            <dl className="intake-preview-fields">
                {!readOnly && <div>
                    <dt>Next Action</dt>
                    <dd>{getNextActionLabel(previewLead)}</dd>
                </div>}
                <div>
                    <dt>Owner</dt>
                    <dd>{previewLead.assigned_owner_email || '-'}</dd>
                </div>
                <div>
                    <dt>Client State</dt>
                    <dd>{getStateLabel(previewLead) || '-'}</dd>
                </div>
                {!readOnly && <div>
                    <dt>Documents</dt>
                    <dd>{getDocumentLabel(previewLead)}</dd>
                </div>}
                {previewLead.promotion_batch?.status &&
        <div>
                    <dt>Batch</dt>
                    <dd>{batchResolutionLabel || getPromotionBatchStatusLabel(previewLead.promotion_batch)}</dd>
                </div>}
                <div>
                    <dt>Last Inbound</dt>
                    <dd>{formatTableDate(previewLead.last_inbound_contact_at)}</dd>
                </div>
                <div>
                    <dt>Updated</dt>
                    <dd>{formatTableDate(previewLead.updated_at)}</dd>
                </div>
            </dl>
            {previewLead.staff_review_reason &&
      <div className="intake-preview-note">
                    <span>Review Reason</span>
                    <p>{previewLead.staff_review_reason}</p>
                </div>}
            {previewLead.final_recommendation_summary &&
      <div className="intake-preview-note">
                    <span>Final Recommendation</span>
                    <p>{previewLead.final_recommendation_summary}</p>
                </div>}
            {!readOnly && <IntakePreviewPipeline payload={payload} lead={previewLead} loading={loading} />}
            <div className="intake-preview-panel__actions">
                {!readOnly && <button
          type="button"
          className="intake-button intake-button--primary"
          disabled={Boolean(nextStageDisabledReason)}
          title={nextStageDisabledReason || `Run ${nextStage?.stage?.label || 'the current stage'}.`}
          aria-label={`Run next stage for ${getLeadLabel(previewLead)}`}
          onClick={(event) => onRunNextStage(event, previewLead, payload)}>
                    <AppIcon icon={nextStageRunning ? 'pi pi-spin pi-spinner' : nextStageAiLocked ? 'pi pi-lock' : 'pi pi-play'} aria-hidden="true" />
                    <span>{nextStageRunning ? 'Running Stage' : 'Run Next Stage'}</span>
                </button>}
                {!readOnly && <IntakeRunButton
          lead={previewLead}
          runningPipelineLeadId={runningPipelineLeadId || runningStageLeadId}
          onRunRemaining={onRunRemaining}
          pipelineStages={previewPipelineStages}
          runRemainingDisabledReason={runRemainingDisabledReason} />}
                <Link to={`/intake/leads/${previewLead.id}`} className="intake-button intake-button--primary">
                    <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
                    <span>Open full detail</span>
                </Link>
            </div>
        </div>);

}



function IntakePreviewPipeline({ payload, lead, loading }) {
  const pipelinePayload = payload?.pipeline_preview || payload || {};
  const stages = buildPreviewPipelineStages(payload, lead);
  const hasPipelineData = Boolean(payload) && (stages.length > 0 || (pipelinePayload.pipeline_runs || []).length > 0);
  const pipelineRun = getLatestRun(pipelinePayload?.pipeline_runs || []);
  const versionLabel = getPipelineVersionLabel(pipelinePayload?.pipeline_version || pipelinePayload?.latest_pipeline_version);
  if (loading && !payload) {
    return (
      <section className="intake-preview-pipeline" aria-label="Intake pipeline">
        <div className="intake-preview-pipeline__header">
          <div>
            <span>Pipeline</span>
            <strong>Loading pipeline...</strong>
          </div>
        </div>
      </section>
    );
  }
  if (!hasPipelineData) {
    return (
      <section className="intake-preview-pipeline" aria-label="Intake pipeline">
        <div className="intake-preview-pipeline__header">
          <div>
            <span>Pipeline</span>
            <strong>No pipeline activity yet</strong>
          </div>
          <small>{titleize(lead?.current_stage) || 'Not started'}</small>
        </div>
      </section>
    );
  }
  return (
    <section className="intake-preview-pipeline" aria-label="Intake pipeline">
      <div className="intake-preview-pipeline__header">
        <div>
          <span>Pipeline</span>
          <strong>{versionLabel}</strong>
        </div>
        <small>{pipelineRun ? titleize(pipelineRun.status) : `${stages.length} stages`}</small>
      </div>
      <ol className="intake-preview-pipeline__stages">
        {stages.map((item, index) => {
          const runTime = item.latestRun?.completed_at || item.latestRun?.updated_at || item.latestRun?.started_at || item.latestRun?.created_at;
          return (
            <li
              key={item.stage.key || `${item.stage.label}-${index}`}
              className={`intake-preview-pipeline-stage intake-preview-pipeline-stage--${item.state.tone}`}>
              <div className="intake-preview-pipeline-stage__marker">
                <AppIcon icon={item.state.icon} aria-hidden="true" />
              </div>
              <div className="intake-preview-pipeline-stage__body">
                <div className="intake-preview-pipeline-stage__title">
                  <strong>{item.stage.label || titleize(item.stage.key)}</strong>
                  <span>{item.state.label}</span>
                </div>
                {item.stage.description && <p>{item.stage.description}</p>}
                <div className="intake-preview-pipeline-stage__meta">
                  {runTime && <small>{formatTableDate(runTime)}</small>}
                  {item.agentCount > 0 && <small>{item.agentCount} agents</small>}
                  {item.activeAgentCount > 0 && <small>{item.activeAgentCount} active</small>}
                  {item.failedAgentCount > 0 && <small>{item.failedAgentCount} failed</small>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}



function IntakeFullTable({
  leads,
  loading,
  visibleColumns,
  filters,
  filterOptions,
  sortField,
  sortOrder,
  runningPipelineLeadId,
  onSort,
  onFilterChange,
  onRunRemaining,
  runRemainingDisabledReason,
  onOpenLead
}) {
  return (
    <div className="intake-table-wrap">
            <table className="intake-table">
                <colgroup>
                    <col style={{ width: ACTION_COLUMN_WIDTH }} />
                    {visibleColumns.map((column) =>
          <col key={column.key} style={{ width: column.width }} />
          )}
                </colgroup>
                <thead>
                    <tr>
                        <th className="intake-table__actions-header">
                            <div className="intake-th intake-th--actions">
                                <span>Actions</span>
                            </div>
                        </th>
                        {visibleColumns.map((column) => {
            const isSorted = sortField === column.sortField;
            return (
              <th key={column.key}>
                                <div className="intake-th">
                                    <button
                  type="button"
                  className="intake-sort-button"
                  onClick={() => onSort(column.sortField)}
                  disabled={!column.sortField}
                  aria-label={column.sortField ? `Sort ${column.label}` : `${column.label} not sortable`}>

                                        <span className="intake-sort-label">{column.label}</span>
                                        <span className="intake-sort-meta">
                                            <AppIcon icon={getSortIconClass(isSorted, sortOrder)} aria-hidden="true" />
                                            <span>{isSorted ? sortOrder === -1 ? 'Descending' : 'Ascending' : 'Unsorted'}</span>
                                        </span>
                                    </button>
                                    <IntakeColumnFilter
                  column={column}
                  filters={filters}
                  filterOptions={filterOptions}
                  onChange={onFilterChange} />

                                </div>
                            </th>);

          })}
                    </tr>
                </thead>
                <tbody>
                    {loading && leads.length === 0 &&
          <tr>
                            <td colSpan={(visibleColumns.length || 1) + 1} className="intake-empty">Loading intake leads...</td>
                        </tr>}

                    {!loading && leads.length === 0 &&
          <tr>
                            <td colSpan={(visibleColumns.length || 1) + 1} className="intake-empty">No intake leads found.</td>
                        </tr>}

                    {leads.length > 0 && leads.map((lead) =>
          {
            return (
          <tr
            key={lead.id}
            data-testid="intake-row"
            tabIndex={0}
            onClick={() => onOpenLead(lead)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenLead(lead);
              }
            }}>

                            <td className="intake-table__actions-cell">
                                <IntakeRunButton
                  lead={lead}
                  runningPipelineLeadId={runningPipelineLeadId}
                  onRunRemaining={onRunRemaining}
                  runRemainingDisabledReason={runRemainingDisabledReason}
                  table />
                            </td>
                            {visibleColumns.map((column) =>
            <td key={column.key} className={getCellClassName(column)} title={getCellText(lead, column)}>
                                    <IntakeCell column={column} row={lead} />
                                </td>
            )}
                        </tr>
            );
          }
          )}
                </tbody>
            </table>
        </div>);

}



function IntakeColumnFilter({ column, filters, filterOptions, onChange }) {
  const filter = column.filter;
  if (!filter) {
    return <div className="intake-column-filter-placeholder" aria-hidden="true"></div>;
  }

  if (filter.type === 'dateRange') {
    const startValue = filters[filter.startField] || '';
    const endValue = filters[filter.endField] || '';
    const summary = startValue || endValue ? 'Range' : 'Any';

    return (
      <details className="intake-header-filter">
                <summary aria-label={`Filter ${column.label}`}>
                    <span>{summary}</span>
                    <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
                </summary>
                <div className="intake-header-filter__menu" role="group" aria-label={`${column.label} date range`}>
                    <label className="intake-header-filter__date">
                        <span>From</span>
                        <input
              type="date"
              value={startValue}
              onChange={(event) => onChange(filter.startField, event.target.value)}
              aria-label={`Filter ${column.label} from`} />

                    </label>
                    <label className="intake-header-filter__date">
                        <span>To</span>
                        <input
              type="date"
              value={endValue}
              onChange={(event) => onChange(filter.endField, event.target.value)}
              aria-label={`Filter ${column.label} to`} />

                    </label>
                </div>
            </details>);

  }

  const value = filters[filter.field] || DEFAULT_FILTERS[filter.field] || '';
  const label = filter.label || column.label;

  if (filter.type === 'multiselect') {
    const options = filterOptions[filter.optionsKey] || [{ label: 'All', value: 'all' }];
    return (
      <IntakeHeaderMultiSelectFilter
        label={column.label}
        values={value}
        options={options}
        onChange={(nextValues) => onChange(filter.field, nextValues)} />);

  }

  if (filter.type === 'select') {
    const options = filterOptions[filter.optionsKey] || [{ label: 'All', value: 'all' }];
    return (
      <select
        className="intake-column-filter-input"
        value={value}
        onChange={(event) => onChange(filter.field, event.target.value)}
        aria-label={`Filter ${column.label}`}>

                {options.map((option) =>
        <option key={option.value} value={option.value}>{option.label}</option>
        )}
            </select>);

  }

  return (
    <input
      className="intake-column-filter-input"
      type={filter.type === 'date' ? 'date' : 'text'}
      value={value}
      onChange={(event) => onChange(filter.field, event.target.value)}
      placeholder={filter.placeholder || label}
      aria-label={`Filter ${column.label}`} />);


}



function IntakeHeaderMultiSelectFilter({ label, values, options, onChange }) {
  const selectedValues = Array.isArray(values) ? values : [];
  const availableOptions = (options || []).filter((option) => option.value !== 'all');
  const allValues = availableOptions.map((option) => option.value);
  const summary = selectedValues.length > 0 ? `${selectedValues.length} selected` : 'Any';

  const toggleValue =

  (optionValue) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((value) => value !== optionValue));
    } else {
      onChange(selectedValues.concat(optionValue));
    }
  };

  const selectAllOptions =

  () => {
    onChange(allValues);
  };

  const deselectAllOptions =

  () => {
    onChange([]);
  };

  return (
    <details className="intake-header-filter">
            <summary aria-label={`Filter ${label}`}>
                <span>{summary}</span>
                <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
            </summary>
            <div className="intake-header-filter__menu" role="group" aria-label={`${label} options`}>
                {availableOptions.length > 0 &&
        <div className="intake-header-filter__bulk-actions">
        <button
          type="button"
          className="intake-header-filter__select-all"
          onClick={selectAllOptions}
          disabled={selectedValues.length === allValues.length}
          aria-label={`Select all ${label} options`}>
                    Select all
                </button>
                    <button
          type="button"
          className="intake-header-filter__select-all"
          onClick={deselectAllOptions}
          disabled={selectedValues.length === 0}
          aria-label={`Deselect all ${label} options`}>
                        Deselect all
                    </button>
                </div>}

                {availableOptions.length === 0 &&
        <div className="intake-header-filter__empty">No options</div>}

                {availableOptions.map((option) =>
        <label key={option.value} className="intake-header-filter__option">
                    <input
            type="checkbox"
            checked={selectedValues.includes(option.value)}
            onChange={() => toggleValue(option.value)}
            aria-label={`${label} ${option.label}`} />
                    <span aria-hidden="true">{option.label}</span>
                </label>
        )}
            </div>
        </details>);

}



function IntakeCell({ column, row }) {
  switch (column.key) {
    case 'lead':
      return <span>{row.prospect_name || row.lead_title || '-'}</span>;
    case 'status':
      return <span className={`intake-badge ${getStatusClass(row.status)}`}>{titleize(row.status)}</span>;
    case 'dedupe':
      return (
        <span
          className={`intake-badge ${getDuplicateStatusClass(row.duplicate_review_status)}`}
          title={row.top_duplicate_score ? `Top duplicate score ${row.top_duplicate_score}` : undefined}>
          {getDuplicateStatusLabel(row)}
        </span>);
    case 'stage':
      return <span className={`intake-badge ${getStageClass(row.current_stage)}`}>{titleize(row.current_stage)}</span>;
    case 'category':
      return <span>{titleize(row.category)}</span>;
    case 'defendant':
      return <span>{row.defendant_name_raw || row.defendant_name_normalized || '-'}</span>;
    case 'state':
      return <span>{row.state_client || row.state_defendant || row.state_events || '-'}</span>;
    case 'source':
      return <span>{titleize(row.source)}</span>;
    case 'pipedrive':
      return <span>{getPipedriveText(row)}</span>;
    case 'documents': {
      const count = Number(row.document_count || 0);
      const className = count > 0 ? 'intake-badge--info' : 'intake-badge--muted';
      return <span className={`intake-badge ${className} app-data-value`}>{count}</span>;
    }
    case 'owner':
      return <span>{row.assigned_owner_email || '-'}</span>;
    case 'next_action':
      return <span className="app-data-value">{row.next_action_label || formatTableDate(row.next_action_at)}</span>;
    case 'last_inbound':
      return <span className="app-data-value">{formatTableDate(row.last_inbound_contact_at)}</span>;
    case 'round':
      return <span className="app-data-value">{row.follow_up_round || 0}</span>;
    case 'review':
      return row.staff_review_required ?
      <span className="intake-badge intake-badge--danger">Review</span> :
      <span className="intake-muted">No</span>;
    case 'class_action':
      return row.potential_class_action ?
      <span className="intake-badge intake-badge--violet">Potential</span> :
      <span className="intake-muted">No</span>;
    case 'decision':
      return <span>{getDecisionText(row)}</span>;
    case 'created':
      return <span className="app-data-value">{formatTableDate(row.created_at)}</span>;
    case 'updated':
      return <span className="app-data-value">{formatTableDate(row.updated_at)}</span>;
    default:
      return null;
  }
}

export default IntakeQueue;
